// @flow

/**
 * Module dependencies.
 */

import { Col, Container, Row, Section } from 'client/components/core/grid';
import { List, ListItem } from 'client/components/core/lists';
import { branch, translator } from 'core/utils';
import { colors, columns, media, shadows, units } from 'client/styles';
import { compose, defaultProps } from 'recompose';
import { position, size } from 'polished';
import { prop } from 'styled-tools';
import BackgroundImage from 'client/components/core/images/background-image';
import FormTransferRequest from 'client/components/forms/form-transfer-request';
import Icon from 'client/components/core/icon';
import Layout from 'client/components/core/layout';
import Raw from 'react-raw-html';
import React, { Component } from 'react';
import Text from 'client/components/core/type';
import entities from 'client/core/entities';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  bgColor: string,
  formTransferRequestSubject: string,
  translate: Function,
  underlayColor?: string
};

/**
 * `Panel` component.
 */

const Panel = styled.div`
  background-color: ${colors.brandSecondary};
  box-shadow: ${shadows.medium};
  min-height: 400px;
  padding: ${units(5)} 0;
  position: relative;

  &:before{
    ${position('absolute', '0', '0', '0', '0')};

    background-color: ${colors.brandSecondary};
    content: '';
    opacity: 0.95;
    z-index: 1;
  }

  ${media.min.lg`box-shadow: none;`};
`;

/**
 * `ContentWrapper` component.
 */

const ContentWrapper = styled.div`
  color: ${colors.white};
  display: inline-block;
  margin: ${units(3)} 0;
  position: relative;
  width: 100%;
  z-index: 3;

  ${media.min.sm`margin: ${units(8)} 0;`};
`;

/**
 * `StyledList` component.
 */

const StyledList = styled(List)`
  margin-top: ${units(2)};

  ${media.min.md`margin-top: ${units(8)};`}
`;

/**
 * `Item` component.
 */

const Item = styled.div`
  margin: ${units(2)} 0;
  padding-left: ${units(5)};
  position: relative;

  span {
    ${position('absolute', '0', null, '0', '0')};
    ${size(units(3), units(3))};
  }
`;

/**
 * `ColFormRequest` component.
 */

const ColFormRequest = styled(Col)`
  z-index: 4;

  ${media.min.lg`
    margin-left: ${columns(-2)}%;
  `}
`;

/**
 * `VerticalAlignment` component.
 */

const VerticalAlignment = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
`;

/**
 * `Underlay` component.
 */

const Underlay = styled.div`
  ${position('absolute', '0', '0', null, '0')};

  background-color: ${prop('color')};
  height: 15vh;

  ${media.min.sm`
    height: calc((100% - 414px) / 2);
  `}
`;

/**
 * `Transferring` component.
 */

class Transferring extends Component {

  /**
   * Props.
   */

  props: Props;

  /**
   * Render list items.
   */

  renderListItems(items: Array<string>): React$Element<*> {
    return (
      <StyledList>
        {items.map((item: string, index: number): React$Element<*> => (
          <ListItem
            block
            key={index}
          >
            <Item>
              <Icon
                color={colors.brandPrimary}
                icon={'check-circle'}
              />

              {item}
            </Item>
          </ListItem>
        ))}
      </StyledList>
    );
  }

  /**
   * Render.
   */

  render(): React$Element<*> {
    const { bgColor, formTransferRequestSubject, translate, underlayColor } = this.props;
    const { transferringItems } = entities(translate);

    return (
      <Section bgColor={bgColor}>
        {branch(underlayColor, () => <Underlay color={underlayColor} />)}

        <Container>
          <Row>
            <VerticalAlignment>
              <Col
                lg={8}
                lgOffset={1}
                xs={10}
                xsOffset={1}
              >
                <Panel>
                  <BackgroundImage filename={'building'} />

                  <ContentWrapper>
                    <Row>
                      <Col
                        lg={6}
                        md={8}
                        mdOffset={1}
                        xs={10}
                        xsOffset={1}
                      >
                        <Layout paddingLeftLg={`${columns(1)}%`}>
                          <Text.H2 color={colors.brandPrimary}>
                            <Raw.span>
                              {translate('transferring.title')}
                            </Raw.span>
                          </Text.H2>

                          {this.renderListItems(transferringItems)}
                        </Layout>
                      </Col>
                    </Row>
                  </ContentWrapper>
                </Panel>
              </Col>

              <ColFormRequest
                lg={4}
                xs={10}
                xsOffset={1}
              >
                <FormTransferRequest subject={formTransferRequestSubject} />
              </ColFormRequest>
            </VerticalAlignment>
          </Row>
        </Container>
      </Section>
    );
  }

}

/**
 * Export `Transferring`.
 */

export default compose(
  defaultProps({
    bgColor: 'main'
  }),
  translator()
)(Transferring);



// WEBPACK FOOTER //
// ../src/client/components/transferring/index.js